import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Pagination,
  IconButton,
  Tooltip,
  Skeleton,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import axios from '../utils/axios';
import PnlText from '../design/PnlText';
import UpdatedStamp from '../design/UpdatedStamp';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { pnlColors, surfaceTints } from '../design/theme';

interface DailyActivityItem {
  Id: number;
  Date: string;
  TotalProfitAndLoss: number;
  TotalTransactions: number;
  TraderId: number;
}

interface DailyActivityProps {
  traderId?: number;
}

const itemsPerPage = 10;

/** Map each benchmark date (after the first) to its % change from the prior close. */
export const computeDailyChanges = (
  points: { date: string; close: number }[]
): Record<string, number> => {
  const changes: Record<string, number> = {};
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1].close;
    if (prev > 0) changes[points[i].date] = ((points[i].close - prev) / prev) * 100;
  }
  return changes;
};

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

const DailyActivity: React.FC<DailyActivityProps> = ({ traderId = 1 }) => {
  const [activities, setActivities] = useState<DailyActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [deposits, setDeposits] = useState<number | null>(null);
  const [spyChanges, setSpyChanges] = useState<Record<string, number>>({});
  const fetchSeq = useRef(0);

  // SPY context line per day. One request per page of rows, padded back 7
  // calendar days so the first visible day has a prior close. Any failure
  // just means no SPY captions — it must never fail the panel.
  useEffect(() => {
    if (activities.length === 0) return;
    let cancelled = false;
    const dates = activities.map((a) => a.Date).sort();
    const start = new Date(dates[0]);
    start.setDate(start.getDate() - 7);
    axios.get('/benchmark', {
      params: {
        symbol: 'SPY',
        start_date: start.toISOString().split('T')[0],
        end_date: dates[dates.length - 1],
      },
    })
      .then((res) => { if (!cancelled) setSpyChanges(computeDailyChanges(res.data?.data ?? [])); })
      .catch((err) => { console.error('Error fetching SPY benchmark:', err); });
    return () => { cancelled = true; };
  }, [activities]);

  // Deposits are the denominator for each day's percentage. Fetched once per
  // trader (they barely move), and deliberately outside the 60s poll. Any
  // failure just means no percentages — it must never fail the panel.
  useEffect(() => {
    let cancelled = false;  // set on unmount/trader change so a late response is dropped
    setDeposits(null);
    axios.get(`/portfolio?trader_id=${traderId}`)
      .then((res) => { if (!cancelled) setDeposits(res.data?.Deposits ?? null); })
      .catch((err) => { console.error('Error fetching portfolio deposits:', err); });
    return () => { cancelled = true; };
  }, [traderId]);

  const fetchActivities = useCallback(async (silent = false) => {
    const seq = ++fetchSeq.current;
    if (!silent) setLoading(true);
    try {
      const skip = (currentPage - 1) * itemsPerPage;
      const response = await axios.get(`/daily-activities?trader_id=${traderId}&skip=${skip}&limit=${itemsPerPage}`);
      if (seq !== fetchSeq.current) return;  // stale response; a newer request is in flight
      setActivities(response.data.activities || response.data);
      setTotalCount(response.data.total || response.data.length);
      setUpdatedAt(new Date());
      setError(null);
    } catch (err) {
      if (seq !== fetchSeq.current) return;
      console.error('Error fetching daily activities:', err);
      if (!silent) setError('Failed to fetch daily activities');
    } finally {
      if (seq === fetchSeq.current && !silent) setLoading(false);
    }
  }, [currentPage, traderId]);

  useEffect(() => { fetchActivities(); }, [fetchActivities]);
  useAutoRefresh(() => fetchActivities(true));

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  // Shrink clamp: if the current page falls past the last page after data
  // changes (e.g. a silent refresh returns fewer rows), snap back. Recomputed
  // inside the effect (itemsPerPage is module-scope) so the deps stay complete.
  useEffect(() => {
    const pages = Math.ceil(totalCount / itemsPerPage);
    if (!loading && pages >= 1 && currentPage > pages) setCurrentPage(pages);
  }, [loading, totalCount, currentPage]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const maxAbsPnl = Math.max(...activities.map((a) => Math.abs(a.TotalProfitAndLoss)), 0) || 1;
  // Null unless we have a usable denominator, which is also the render guard.
  const depositsBase = deposits !== null && deposits > 0 ? deposits : null;

  const header = (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      flexDirection={{ xs: 'column', sm: 'row' }}
      gap={{ xs: 2, sm: 0 }}
      mb={3}
    >
      <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.dark' }}>
        Daily Activity
      </Typography>
      <Box display="flex" alignItems="center" gap={1}>
        <UpdatedStamp updatedAt={updatedAt} />
        <Chip
          label={`${totalCount} activities`}
          color="primary"
          variant="outlined"
          size="small"
        />
        <Tooltip title="Refresh activities">
          <IconButton onClick={() => fetchActivities()} size="small" color="primary">
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  if (error) {
    return (
      <Box sx={{ width: '100%' }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.dark', mb: 3 }}>
          Daily Activity
        </Typography>
        <Paper sx={{ textAlign: 'center', p: 4, borderRadius: 2, boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)' }}>
          <Typography color="error" variant="h6" mb={2}>{error}</Typography>
          <Tooltip title="Refresh activities">
            <IconButton onClick={() => fetchActivities()} color="primary" aria-label="Retry">
              <Refresh />
            </IconButton>
          </Tooltip>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {header}

      <Paper
        sx={{
          borderRadius: 2,
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          overflow: 'hidden',
          mb: 3,
        }}
      >
        {loading ? (
          <>
            {Array.from({ length: 10 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={44} sx={{ mx: 3, my: 1.5 }} />
            ))}
          </>
        ) : activities.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              py: 4,
              minHeight: 120,
            }}
          >
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No daily activities found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This trader hasn't had any daily activity yet.
            </Typography>
          </Box>
        ) : (
          activities.map((activity) => (
            <Box
              key={activity.Id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                px: 3,
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:last-child': { borderBottom: 'none' },
                '&:hover': { backgroundColor: surfaceTints.rowHover },
              }}
            >
              <Box sx={{ width: 170, flexShrink: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDate(activity.Date)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {activity.TotalTransactions} transactions
                </Typography>
              </Box>
              <Box
                aria-hidden="true"
                sx={{ flexGrow: 1, height: 8, backgroundColor: '#ECEFF4', borderRadius: 4, overflow: 'hidden' }}
              >
                <Box
                  data-testid="pnl-bar-fill"
                  sx={{
                    height: '100%',
                    borderRadius: 4,
                    width: `${(Math.abs(activity.TotalProfitAndLoss) / maxAbsPnl) * 100}%`,
                    backgroundColor: activity.TotalProfitAndLoss >= 0 ? pnlColors.gain : pnlColors.loss,
                    opacity: 0.85,
                  }}
                />
              </Box>
              <Box sx={{ width: 130, textAlign: 'right', flexShrink: 0 }}>
                <PnlText value={activity.TotalProfitAndLoss} format="currency" variant="body2" />
                {depositsBase !== null && (
                  <Box>
                    <PnlText
                      value={(activity.TotalProfitAndLoss / depositsBase) * 100}
                      format="percent"
                      variant="caption"
                    />
                  </Box>
                )}
                {spyChanges[activity.Date] !== undefined && (
                  <Box display="flex" justifyContent="flex-end" alignItems="baseline" gap={0.5}>
                    <Typography variant="caption" color="text.secondary">SPY</Typography>
                    <PnlText value={spyChanges[activity.Date]} format="percent" variant="caption" />
                  </Box>
                )}
              </Box>
            </Box>
          ))
        )}
      </Paper>

      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="large"
            sx={{
              '& .MuiPaginationItem-root': {
                borderRadius: 2,
                fontWeight: 'bold',
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default DailyActivity;
